import { useEffect, useRef } from 'react';
import { useTwin } from '../context/TwinContext';
import { motorHealthEngine } from './ewmaEngine';
import { alertStore, AlertStore, AlertStage } from './alertStore';
import { correlate } from './correlationEngine';

export function useEWMAMonitor() {
  const { getActiveTwin } = useTwin();
  const prevTwinId = useRef<string | null>(null);

  useEffect(() => {
    const interval = setInterval(() => {
      const twin = getActiveTwin();
      if (!twin) return;

      if (twin.id !== prevTwinId.current) {
        motorHealthEngine.reset();
        alertStore.clearAllEWMA();
        alertStore.setCorrelation(null);
        prevTwinId.current = twin.id;
      }

      // ── Per-machine processing ──────────────────────────────────
      const machineSummaries: { machineId: string; machineName: string; stage: string; avgSlope: number; patterns: string[]; healthScore: number }[] = [];

      twin.machines.forEach(machine => {
        const sensors = machine.sensors.map(s => ({
          sensorId: s.sensorId,
          name:     s.name,
          value:    twin.simulatedMachineData?.[machine.machineId]?.[s.sensorId] ?? s.value,
        }));

        const fused   = motorHealthEngine.fuse(machine.machineId, sensors);
        const alertId = `fused-${machine.machineId}`;

        // Collect summary for correlation
        machineSummaries.push({
          machineId:   machine.machineId,
          machineName: machine.name,
          stage:       fused.stage,
          avgSlope:    fused.avgSlope,
          patterns:    fused.detectedPatterns.map(p => p.name),
          healthScore: fused.healthScore,
        });

        if (fused.stage === 'CALIBRATING' || fused.stage === 'NORMAL') {
          alertStore.clearEWMAAlert(alertId);
          return;
        }

        const sensorIdMap: Record<string, string> = {};
        machine.sensors.forEach(s => { sensorIdMap[s.name] = s.sensorId; });

        const alert = AlertStore.buildFusedAlert({
          machineId:            machine.machineId,
          machineName:          machine.name,
          stage:                fused.stage as AlertStage,
          dominantSensor:       fused.dominantSensor,
          healthScore:          fused.healthScore,
          confidence:           fused.confidence,
          confidenceBreakdown:  fused.confidenceBreakdown,
          avgSlope:             fused.avgSlope,
          ttf:                  fused.ttf,
          sensorResults:        fused.sensorResults,
          sensorIdMap,
          detectedPatterns:     fused.detectedPatterns,
          baselineShiftSensors: fused.baselineShiftSensors,
        });

        alertStore.setEWMAAlert(alert);
      });

      // ── Cross-machine correlation ───────────────────────────────
      const correlation = correlate(machineSummaries);
      alertStore.setCorrelation(correlation);
    }, 2000);

    return () => clearInterval(interval);
  }, [getActiveTwin]);
}
